import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  ButtonBase,
  Skeleton,
  Tooltip,
  Typography,
} from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import { useTitle } from "utils/UseTitle";
import title from "global/constants/TitleConstants";
import strings from "global/constants/StringConstants";
import notifiers from "global/constants/NotificationConstants";
import CustomInput from "global/components/CustomInput/CustomInput";
import CustomLoader from "global/components/CustomLoader/CustomLoader";
import { handleErrorMessage, openSuccessNotification } from "helpers/methods";
import { hasAccessTo } from "layouts/Config";
import Unauthorized from "screens/Shared/Unauthorized";
import { useFeatureState, FeatureState } from "hooks/use-feature";
import FeatureKeys from "global/constants/FeatureKeys";
import UpgradeModal from "global/components/UpgradeModal/UpgradeModal";
import MaintenanceBanner from "global/components/MaintenanceBanner/MaintenanceBanner";
import { useAppSelector } from "redux/hooks";
import { selectUserName } from "redux/authSlice";
import { useAriaLive } from "hooks/use-aria-live";
import { LiveRegion } from "components/live-region";
import SettingsLayout from "../components/SettingsLayout";
import {
  BrandingSettings,
  fetchBrandingSettings,
  updateBrandingSettings,
  uploadBrandingLogo,
} from "./BrandingService";
import EmailPreview, { PRODUCT_NAME } from "./component/EmailPreview";
import LogoUpload from "./component/LogoUpload";
import brandingStyles from "./Branding.styles";
import brandingTokens from "./Branding.tokens";
import FeatureBanner from "global/components/FeatureBanner/FeatureBanner";

const styles = brandingStyles;
const {
  brandColor: brandColorTokens,
  field,
  skeleton,
  toggle,
} = brandingTokens;

const DEFAULT_PRIMARY_COLOR = "#DC4313";
const DEFAULT_BUTTON_TEXT_COLOR = "#FFFFFF";
const DEFAULT_BACKGROUND_COLOR = "#F3F4F6";
/** Below this ratio the CTA label is unreadable on the chosen brand colour. */
const MIN_CONTRAST_RATIO = 3.0;
/** Below this ratio the combination is legible but flagged as low contrast. */
const RECOMMENDED_CONTRAST_RATIO = 4.5;
const LOGO_HINT = "PNG or JPG · recommended 180×80px · max 1 MB";

const DEFAULT_BRANDING: BrandingSettings = {
  name: "",
  appUrl: "",
  logoUrl: "",
  primaryColor: DEFAULT_PRIMARY_COLOR,
  buttonTextColor: DEFAULT_BUTTON_TEXT_COLOR,
  backgroundColor: DEFAULT_BACKGROUND_COLOR,
  footerText: "",
  senderEmail: "",
  senderName: "",
  enabled: false,
};

const getValidHexColor = (color: any, fallback: string) => {
  if (!color || typeof color !== "string") return fallback;
  const trimmed = color.trim();
  if (!trimmed || trimmed === "null" || trimmed === "undefined")
    return fallback;
  if (trimmed.startsWith("#")) return trimmed;
  if (/^[0-9A-Fa-f]{3,6}$/.test(trimmed)) return `#${trimmed}`;
  return fallback;
};

const getLuminance = (hex: string) => {
  let r = 0;
  let g = 0;
  let b = 0;
  const cleanHex = hex.replace("#", "");
  if (cleanHex.length === 3) {
    r = parseInt(cleanHex[0] + cleanHex[0], 16);
    g = parseInt(cleanHex[1] + cleanHex[1], 16);
    b = parseInt(cleanHex[2] + cleanHex[2], 16);
  } else if (cleanHex.length === 6) {
    r = parseInt(cleanHex.substring(0, 2), 16);
    g = parseInt(cleanHex.substring(2, 4), 16);
    b = parseInt(cleanHex.substring(4, 6), 16);
  }
  const [rs, gs, bs] = [r, g, b].map((channel) => {
    const c = channel / 255;
    return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
  });
  return 0.2126 * rs + 0.7152 * gs + 0.0722 * bs;
};

const getContrastRatio = (color1: string, color2: string) => {
  const l1 = getLuminance(color1);
  const l2 = getLuminance(color2);
  return (Math.max(l1, l2) + 0.05) / (Math.min(l1, l2) + 0.05);
};

const Branding = () => {
  useTitle(title.BRANDING);
  const userName = useAppSelector(selectUserName);
  const hasAdminAccess = hasAccessTo(
    strings.resources.app,
    strings.adminPermission,
  );

  const brandingState = useFeatureState(FeatureKeys.BRANDING_CUSTOM);
  const isBrandingAvailable =
    brandingState.status === strings.FEATURE_STATUS_AVAILABLE;
  const brandingTooltip = ((state: FeatureState): string => {
    if (state.status === strings.FEATURE_STATUS_NOT_IN_PLAN)
      return strings.FEATURE_GATE_UPSELL_TOOLTIP;
    if (state.status === strings.FEATURE_STATUS_ADMIN_DISABLED)
      return (
        state.disabledMessage ??
        strings.FEATURE_GATE_TEMPORARILY_UNAVAILABLE_TOOLTIP
      );
    return "";
  })(brandingState);

  const [loader, setLoader] = useState(false);
  const [saveLoader, setSaveLoader] = useState(false);
  const [brandingUpgradeOpen, setBrandingUpgradeOpen] = useState(false);
  const [brandingMaintenanceOpen, setBrandingMaintenanceOpen] = useState(false);
  const [initialSettings, setInitialSettings] =
    useState<BrandingSettings>(DEFAULT_BRANDING);
  const [settings, setSettings] = useState<BrandingSettings>(DEFAULT_BRANDING);
  const { announcement, priority, announce } = useAriaLive();

  const effectiveEnabled = isBrandingAvailable && settings.enabled;
  const controlsDisabled = !effectiveEnabled || saveLoader;

  useEffect(() => {
    if (hasAdminAccess) {
      loadSettings();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasAdminAccess]);

  const contrastRatio = useMemo(
    () => getContrastRatio(settings.primaryColor, settings.buttonTextColor),
    [settings.primaryColor, settings.buttonTextColor],
  );
  const isUnreadable = contrastRatio < MIN_CONTRAST_RATIO;

  const isDirty = useMemo(
    () => JSON.stringify(settings) !== JSON.stringify(initialSettings),
    [settings, initialSettings],
  );

  const loadSettings = async () => {
    try {
      setLoader(true);
      const data = await fetchBrandingSettings();
      const normalizedData = {
        ...data,
        primaryColor: getValidHexColor(
          data.primaryColor,
          DEFAULT_PRIMARY_COLOR,
        ),
        buttonTextColor: getValidHexColor(
          data.buttonTextColor,
          DEFAULT_BUTTON_TEXT_COLOR,
        ),
        backgroundColor: getValidHexColor(
          data.backgroundColor,
          DEFAULT_BACKGROUND_COLOR,
        ),
      };
      setSettings(normalizedData);
      setInitialSettings(normalizedData);
    } catch (error: any) {
      // Fall back to defaults as the baseline so the user can still configure
      // and save (creating the record server-side) when the GET fails.
      setSettings(DEFAULT_BRANDING);
      setInitialSettings(DEFAULT_BRANDING);
      handleErrorMessage(error);
    } finally {
      setLoader(false);
    }
  };

  const triggerBrandingGate = () => {
    if (isBrandingAvailable) return;
    if (brandingState.status === strings.FEATURE_STATUS_NOT_IN_PLAN) {
      setBrandingUpgradeOpen(true);
    } else {
      setBrandingMaintenanceOpen(true);
    }
  };

  const handleSave = async () => {
    try {
      setSaveLoader(true);
      await updateBrandingSettings(settings);
      setInitialSettings(settings);
      openSuccessNotification(notifiers.BRANDING_SAVE_SUCCESS);
      announce("Branding settings saved.");
    } catch (error: any) {
      handleErrorMessage(error);
      announce("Couldn't save branding settings.", "assertive");
    } finally {
      setSaveLoader(false);
    }
  };

  const handleLogoUpload = async (file: File) => {
    try {
      setSaveLoader(true);
      // Show the local file immediately; swap in the stored path once uploaded.
      const localPreviewUrl = URL.createObjectURL(file);
      setSettings((prev) => ({ ...prev, logoUrl: localPreviewUrl }));

      const formData = new FormData();
      formData.append("file", file);
      const response = await uploadBrandingLogo(formData);

      setSettings((prev) => ({ ...prev, logoUrl: response?.message || "" }));
      openSuccessNotification(notifiers.BRANDING_LOGO_UPLOAD_SUCCESS);
      announce("Logo uploaded.");
    } catch (error: any) {
      handleErrorMessage(error);
      announce("Couldn't upload logo.", "assertive");
    } finally {
      setSaveLoader(false);
    }
  };

  const handleLogoRemove = () => {
    setSettings((prev) => ({ ...prev, logoUrl: "" }));
  };

  const handleFieldChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const { name, value } = event.target;
    setSettings((prev) => ({ ...prev, [name]: value }));
  };

  const handlePrimaryColorChange = (color: string) => {
    setSettings((prev) => ({ ...prev, primaryColor: color }));
  };

  const handleButtonTextColorChange = (color: string) => {
    setSettings((prev) => ({ ...prev, buttonTextColor: color }));
  };

  const handleToggleBranding = () => {
    setSettings((prev) => ({ ...prev, enabled: !prev.enabled }));
  };

  if (!hasAdminAccess) {
    return <Unauthorized permissionDenied={true} />;
  }

  const getSaveButton = () => (
    <Tooltip
      title={brandingTooltip}
      disableHoverListener={isBrandingAvailable}
      arrow
      describeChild
    >
      <Box component="span" sx={{ display: "inline-flex" }}>
        <Button
          id="branding_save_btn"
          variant="contained"
          onClick={handleSave}
          disabled={
            !isDirty ||
            saveLoader ||
            loader ||
            isUnreadable ||
            !isBrandingAvailable
          }
          endIcon={!isBrandingAvailable ? <LockOutlinedIcon /> : undefined}
          sx={styles.primaryButton}
        >
          Save Changes
        </Button>
      </Box>
    </Tooltip>
  );

  const getMasterSwitchCard = () => (
    <Box sx={styles.switchCard}>
      <Box sx={styles.switchCardText}>
        <Typography component="h2" sx={styles.switchCardTitle}>
          Custom Branding
        </Typography>
        <Typography component="div" sx={styles.cardDescription}>
          Apply your logo, colors, and footer to every signing email and page.
        </Typography>
      </Box>
      <Tooltip
        title={brandingTooltip}
        disableHoverListener={isBrandingAvailable}
        arrow
        describeChild
      >
        <Box
          component="span"
          onClick={isBrandingAvailable ? undefined : triggerBrandingGate}
          sx={styles.toggleWrapper}
        >
          <ButtonBase
            disableRipple
            role="switch"
            aria-checked={effectiveEnabled}
            aria-label="Custom Branding"
            disabled={!isBrandingAvailable}
            onClick={handleToggleBranding}
            sx={styles.toggleTrack(effectiveEnabled)}
          >
            <Box sx={styles.toggleKnob(effectiveEnabled)} />
          </ButtonBase>
          {!isBrandingAvailable && <LockOutlinedIcon sx={styles.toggleLock} />}
        </Box>
      </Tooltip>
    </Box>
  );

  const getCompanyLogoCard = () => (
    <Box sx={styles.card}>
      <Box sx={styles.cardHeaderText}>
        <Typography component="h2" sx={styles.cardTitle}>
          Company Logo
        </Typography>
        <Typography component="div" sx={styles.cardDescription}>
          Shown at the top of signing emails and the signer page.
        </Typography>
      </Box>
      <LogoUpload
        hint={LOGO_HINT}
        imageUrl={settings.logoUrl}
        onUpload={handleLogoUpload}
        onRemove={handleLogoRemove}
        disabled={controlsDisabled}
      />
    </Box>
  );

  const getBrandIdentityCard = () => (
    <Box sx={styles.card}>
      <Typography component="h2" sx={styles.cardTitle}>
        Brand Identity
      </Typography>
      <Box sx={styles.fieldGrid}>
        <CustomInput
          id="brandName"
          label="Brand Name"
          name="name"
          required
          placeHolder="e.g. Acme Signing"
          value={settings.name}
          disabled={controlsDisabled}
          propsToInputElement={{ maxLength: 100 }}
          onChange={handleFieldChange}
          labelSx={styles.fieldLabel}
          labelSpacing={field.labelGap}
          sx={styles.textField}
        />
        <CustomInput
          id="appUrl"
          label="App URL"
          name="appUrl"
          type="url"
          placeHolder="https://app.acme.com/"
          value={settings.appUrl}
          disabled={controlsDisabled}
          propsToInputElement={{ maxLength: 500 }}
          onChange={handleFieldChange}
          labelSx={styles.fieldLabel}
          labelSpacing={field.labelGap}
          sx={styles.textField}
        />
      </Box>
    </Box>
  );

  /* Email Sender Details — kept for when the backend exposes a verified
     sending domain. Restyled to the current design so it can be dropped
     straight back in.

  const getSenderDetailsCard = () => (
    <Box sx={styles.card}>
      <Typography component="div" sx={styles.cardTitle}>
        Email Sender Details
      </Typography>
      <Box sx={styles.fieldGrid}>
        <CustomInput
          id="senderName"
          label="Sender Name"
          name="senderName"
          placeHolder="e.g. Acme Corp"
          value={settings.senderName}
          disabled={controlsDisabled}
          propsToInputElement={{ maxLength: 100 }}
          onChange={handleFieldChange}
          labelSx={styles.fieldLabel}
          labelSpacing={field.labelGap}
          sx={styles.textField}
        />
        <CustomInput
          id="senderEmail"
          label="Sender Email"
          name="senderEmail"
          type="email"
          placeHolder="e.g. noreply@acmecorp.com"
          value={settings.senderEmail}
          disabled={controlsDisabled}
          propsToInputElement={{ maxLength: 100 }}
          onChange={handleFieldChange}
          labelSx={styles.fieldLabel}
          labelSpacing={field.labelGap}
          sx={styles.textField}
        />
      </Box>
    </Box>
  );

  */

  /** Label + optional info bubble shared by both colour groups. */
  const getColorGroupLabel = (label: string, tooltip: string) => (
    <Typography component="div" sx={styles.colorGroupLabel}>
      {label}
      <Tooltip title={tooltip} arrow placement="top" describeChild>
        <Box
          component="span"
          role="img"
          aria-label={`${label}: ${tooltip}`}
          tabIndex={0}
          sx={{ display: "inline-flex", alignItems: "center" }}
        >
          <InfoOutlinedIcon sx={styles.colorGroupInfo} />
        </Box>
      </Tooltip>
    </Typography>
  );

  /** Hex read-out chip that opens the native colour picker when clicked. */
  const getColorChip = (
    id: string,
    ariaLabel: string,
    value: string,
    onPick: (color: string) => void,
    offset: boolean,
  ) => (
    <Box
      component="label"
      htmlFor={id}
      sx={styles.colorChip(controlsDisabled, offset)}
    >
      <Box sx={styles.colorChipDot(value)} />
      <Box component="span" sx={styles.colorChipValue}>
        {value}
      </Box>
      <Box
        component="input"
        id={id}
        type="color"
        aria-label={ariaLabel}
        disabled={controlsDisabled}
        value={value}
        onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
          onPick(event.target.value.toUpperCase())
        }
        sx={styles.colorInput}
      />
    </Box>
  );

  const getBrandingColorsCard = () => (
    <Box sx={styles.card}>
      <Box sx={styles.cardHeaderText}>
        <Typography component="h2" sx={styles.cardTitle}>
          Branding Colors
        </Typography>
        <Typography component="div" sx={styles.cardDescription}>
          Used for buttons and links in emails and the signer page.
        </Typography>
      </Box>

      <Box sx={styles.colorGroups}>
        <Box>
          {getColorGroupLabel(
            "Primary Button Color",
            "Applied to primary action buttons (like 'Review & Sign') in transactional emails sent to your recipients.",
          )}
          <Box sx={styles.swatchRow}>
            {brandColorTokens.presets.map((color) => (
              <ButtonBase
                key={color}
                disableRipple
                aria-label={`Brand color ${color}`}
                aria-pressed={
                  settings.primaryColor.toUpperCase() === color.toUpperCase()
                }
                disabled={controlsDisabled}
                onClick={() => handlePrimaryColorChange(color)}
                sx={styles.swatch(
                  color,
                  settings.primaryColor.toUpperCase() === color.toUpperCase(),
                )}
              />
            ))}
            {getColorChip(
              "primaryColorPicker",
              "Custom primary button color",
              settings.primaryColor,
              handlePrimaryColorChange,
              true,
            )}
          </Box>
        </Box>

        <Box>
          {getColorGroupLabel(
            "Button Text Color",
            "Applied to the text inside primary buttons. Ensure high contrast (e.g. white text for dark buttons) for readability.",
          )}
          <Box sx={styles.swatchRow}>
            {getColorChip(
              "buttonTextColorPicker",
              "Custom button text color",
              settings.buttonTextColor,
              handleButtonTextColorChange,
              false,
            )}
          </Box>
        </Box>

        {/* Email Background Color is temporarily disabled — the mail template
            hardcodes the container tint until the backend honours it.

        <Box>
          {getColorGroupLabel(
            "Email Background Color",
            "Used for the email container behind the message card.",
          )}
          <Box sx={styles.swatchRow}>
            {getColorChip(
              "backgroundColorPicker",
              "Custom email background color",
              settings.backgroundColor,
              handleBackgroundColorChange,
              false,
            )}
          </Box>
        </Box>

        */}
      </Box>

      {isUnreadable ? (
        <Typography
          component="div"
          role="alert"
          sx={styles.contrastMessage(true)}
        >
          Button contrast ratio is {contrastRatio.toFixed(1)}:1 (minimum{" "}
          {MIN_CONTRAST_RATIO.toFixed(1)}:1 required). This combination is
          unreadable — pick contrasting colors to save.
        </Typography>
      ) : contrastRatio < RECOMMENDED_CONTRAST_RATIO ? (
        <Typography component="div" sx={styles.contrastMessage(false)}>
          Button contrast ratio is {contrastRatio.toFixed(1)}:1. A ratio of at
          least {RECOMMENDED_CONTRAST_RATIO.toFixed(1)}:1 is recommended for
          good readability.
        </Typography>
      ) : null}
    </Box>
  );

  const getFooterCard = () => (
    <Box sx={styles.card}>
      <Box sx={styles.cardHeaderText}>
        <Typography component="h2" sx={styles.cardTitle}>
          Custom Email Footer
        </Typography>
        <Typography component="div" sx={styles.cardDescription}>
          Closing lines added to the end of every signing email.
        </Typography>
      </Box>
      <CustomInput
        id="footerText"
        name="footerText"
        multiline
        rows={brandingTokens.textarea.rows}
        placeHolder={`e.g. Thanks,\nThe ${settings.name?.trim() || PRODUCT_NAME} Team`}
        value={settings.footerText}
        disabled={controlsDisabled}
        onChange={handleFieldChange}
        sx={styles.footerField}
      />
    </Box>
  );

  const getSkeletonCard = (rows: number) => (
    <Box sx={styles.card}>
      <Box sx={styles.skeletonHeaderText}>
        <Skeleton
          variant="text"
          width={skeleton.titleWidth}
          height={skeleton.titleHeight}
        />
        <Skeleton
          variant="text"
          width={skeleton.descriptionWidth}
          height={skeleton.descriptionHeight}
        />
      </Box>
      <Box sx={styles.fieldGrid}>
        {Array.from({ length: rows }).map((_, index) => (
          <Box key={index}>
            <Skeleton
              variant="text"
              width={skeleton.labelWidth}
              height={skeleton.labelHeight}
              sx={{ mb: field.labelGap }}
            />
            <Skeleton
              variant="rounded"
              width="100%"
              height={field.height}
              sx={{ borderRadius: field.radius }}
            />
          </Box>
        ))}
      </Box>
    </Box>
  );

  const getSkeleton = () => (
    <Box
      sx={styles.columns}
      aria-busy="true"
      aria-label="Loading branding settings"
    >
      <Box sx={styles.settingsColumn}>
        <Box sx={styles.switchCard}>
          <Box sx={styles.skeletonHeaderText}>
            <Skeleton
              variant="text"
              width={skeleton.titleWidth}
              height={skeleton.titleHeight}
            />
            <Skeleton
              variant="text"
              width={skeleton.descriptionWidth}
              height={skeleton.descriptionHeight}
            />
          </Box>
          <Skeleton
            variant="rounded"
            width={toggle.width}
            height={toggle.height}
            sx={{ ml: "auto", flexShrink: 0, borderRadius: "999px" }}
          />
        </Box>
        {/* Company Logo, Brand Identity, Branding Colors, Custom Email Footer */}
        {getSkeletonCard(1)}
        {getSkeletonCard(2)}
        {getSkeletonCard(2)}
        {getSkeletonCard(1)}
      </Box>
      <Box sx={styles.previewColumn}>
        <Box sx={styles.previewHeader}>
          <Skeleton
            variant="text"
            width={skeleton.titleWidth}
            height={skeleton.titleHeight}
          />
        </Box>
        <Skeleton
          variant="rounded"
          width="100%"
          height={skeleton.previewHeight}
          sx={{ borderRadius: brandingTokens.email.radius }}
        />
      </Box>
    </Box>
  );

  return (
    <SettingsLayout
      activeTab="Branding"
      title="Branding"
      // Both small references close the screen with "Save Changes" rather than
      // opening with it; the cards block carries the matching order.
      headerOrder={2}
      headerAction={getSaveButton()}
    >
      <CustomLoader isLoading={saveLoader} />
      <LiveRegion message={announcement} priority={priority} />
      {loader ? (
        getSkeleton()
      ) : (
        <Box sx={styles.columns}>
          <Box sx={styles.settingsColumn}>
            {getMasterSwitchCard()}
            {/*
              Without this the only explanation of a locked screen was the
              upgrade modal, which the reader has to click a disabled toggle to
              reach. Same in-place banner Delegation and Document Settings use.
            */}
            {!isBrandingAvailable && (
              <FeatureBanner
                state={brandingState}
                featureKey={FeatureKeys.BRANDING_CUSTOM}
                variant="compact"
              />
            )}
            <Box sx={styles.gatedCards(effectiveEnabled)}>
              {getCompanyLogoCard()}
              {getBrandIdentityCard()}
              {getBrandingColorsCard()}
              {getFooterCard()}
            </Box>
          </Box>
          <EmailPreview settings={settings} senderName={userName} />
        </Box>
      )}
      <UpgradeModal
        open={brandingUpgradeOpen}
        featureKey={FeatureKeys.BRANDING_CUSTOM}
        onClose={() => setBrandingUpgradeOpen(false)}
      />
      <MaintenanceBanner
        open={brandingMaintenanceOpen}
        message={(brandingState as any).disabledMessage}
        onClose={() => setBrandingMaintenanceOpen(false)}
      />
    </SettingsLayout>
  );
};

export default Branding;
